import { PropsComponent, PropsGeneral } from './../Data';
import {
  computePiecePrice,
  computeWeightWithDensity,
  getPropsKey,
} from '../utils/helpers';
import {
  Material,
  THICKNESS_SLAT_WOOD,
  WIDTH_SLAT_WOOD,
  THICKNESS_SLAT_ALU,
  WIDTH_SLAT_ALU,
  PaneMaterial,
} from './../constants';
import { getPiecesProps } from '../utils/dataStorage';
export class DoorSlat {
  readonly height: number;
  readonly slatMaterial: Material;
  readonly doorMaterial: PaneMaterial;
  readonly custom: boolean;

  static _gapHeight = 50;

  constructor(
    height: number,
    slatMaterial: Material,
    doorMaterial: PaneMaterial,
    custom: boolean,
  ) {
    this.height = height;
    this.slatMaterial = slatMaterial;
    this.doorMaterial = doorMaterial;
    this.custom = custom;
  }

  get typeComponent() {
    return 'DoorSlat';
  }

  get realHeight() {
    return this.height - DoorSlat._gapHeight;
  }

  get realWidth() {
    if (this.slatMaterial === Material.Wood) return WIDTH_SLAT_WOOD;
    return WIDTH_SLAT_ALU;
  }

  get realDepth() {
    if (this.slatMaterial === Material.Wood) return THICKNESS_SLAT_WOOD;
    return THICKNESS_SLAT_ALU;
  }

  static getProperties(material: Material): PropsComponent | void {
    const key = getPropsKey(material);

    if (getPiecesProps().doorSlat.hasOwnProperty(key)) {
      return getPiecesProps().doorSlat[key];
    }
  }

  static computeBasePrice(
    width: number,
    height: number,
    depth: number,
    doorMaterial: PaneMaterial,
    slatMaterial: Material,
    props: PropsComponent,
    propsGen: PropsGeneral,
  ) {
    /**
     * This formula came from the PRO100 Excel linked file
     *
     * Formule :
     * Door MDF and wood slat: ((volume*K1)+K2)*K3 + K4
     * Other door and wood slat: ((volume*K1)+K2)*K3 + K5
     * Alu slat: height * K1
     *
     * volume and height in µ
     *
     */
    height = height > props.minDim ? height : props.minDim;

    const widthMicr = width / 1000;
    const heightMicr = height / 1000;
    const depthMicr = depth / 1000;

    const volume = widthMicr * heightMicr * depthMicr;

    const k1 = props.k1 ?? 0;
    const k2 = props.k2 ?? 0;
    const k3 = props.k3 ?? 0;
    const k4 = props.k4 ?? 0;
    const k5 = props.k5 ?? 0;

    let price = 0;
    if (slatMaterial === Material.Wood && doorMaterial === PaneMaterial.MDF)
      price = (volume * k1 + k2) * k3 + k4;
    else if (
      slatMaterial === Material.Wood &&
      doorMaterial !== PaneMaterial.MDF
    )
      price = (volume * k1 + k2) * k3 + k5;
    else price = heightMicr * k1;

    price *= propsGen.coef;
    price = Math.round(price * 100) / 100;

    return price;
  }

  public computePrice(): number {
    const props: PropsComponent | void = DoorSlat.getProperties(
      this.slatMaterial,
    );
    const propsGen: PropsGeneral = getPiecesProps().general;

    if (!props) return 0;

    const priceForDimensions = DoorSlat.computeBasePrice(
      this.realWidth,
      this.realHeight,
      this.realDepth,
      this.doorMaterial,
      this.slatMaterial,
      props,
      propsGen,
    );

    const priceForBase = DoorSlat.computeBasePrice(
      this.realWidth,
      this.realHeight,
      this.realDepth,
      this.doorMaterial,
      this.slatMaterial,
      props,
      propsGen,
    );

    return computePiecePrice(
      priceForDimensions,
      priceForBase,
      this.custom,
      false,
      propsGen,
    );
  }

  public computeWeight(): number {
    const props: PropsComponent | void = DoorSlat.getProperties(
      this.slatMaterial,
    );

    if (!props) return 0;

    return computeWeightWithDensity(
      this.realWidth,
      this.realHeight,
      this.realDepth,
      props.density,
    );
  }
}
